import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import Header from '../header'
import {
    useGetDevice,
    useGetDeviceHistory,
    useGetDeviceConfigAttributes,
} from '../../../../api/routes/devices'
import { ProcessedData } from './types'
import GraphComponent from './graph-component'
import { Card } from '../../../../components/ui/card'
import { Button } from '../../../../components/ui/button'
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
} from '../../../../components/ui/dropdown-menu'
import { Alert, AlertDescription } from '../../../../components/ui/alert'
import { processData, extractMetadata } from './utils'
import AdvancedTimePicker from './time-picker'

const SingleDeviceHistoryRoute: React.FC = () => {
    const { id: deviceId } = useParams<{ id: string }>()

    // Set default date range to past 24 hours
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    const [dates, setDates] = useState({
        from: yesterday.toISOString(),
        to: today.toISOString(),
    })
    const [processedData, setProcessedData] = useState<ProcessedData>({})
    const [visibleKeys, setVisibleKeys] = useState<string[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [metadata, setMetadata] = useState<any>(null)
    const [searchValue, setSearchValue] = useState('')

    const { data: device, isLoading: isDeviceLoading } = useGetDevice(
        deviceId as string
    )
    const {
        data: deviceHistory,
        isLoading: isHistoryLoading,
        isError: isHistoryError,
        error: historyError,
        refetch: refetchHistory,
    } = useGetDeviceHistory(deviceId || '', dates.from, dates.to)
    const { data: attributes } = useGetDeviceConfigAttributes(
        deviceId as string
    )

    const displayNameMap = useMemo(() => {
        const map: Record<string, string> = {}
        if (attributes) {
            attributes.forEach(
                (attr: { name: string; friendlyName?: string }) => {
                    if (attr.friendlyName) {
                        map[attr.name] = attr.friendlyName
                    }
                }
            )
        }
        return map
    }, [attributes])

    const getDisplayName = (key: string) => displayNameMap[key] || key

    useEffect(() => {
        if (deviceHistory) {
            const data = processData(deviceHistory)
            setProcessedData(data)
            // If visibleKeys is empty or keys have changed, reset to all keys
            const keys = Object.keys(data)
            setVisibleKeys((prev) => {
                if (prev.length === 0 || prev.some((k) => !keys.includes(k))) {
                    return keys
                }
                return prev
            })
            const meta = extractMetadata(deviceHistory)
            setMetadata(meta)
        }
        setLoading(isDeviceLoading || isHistoryLoading)
    }, [deviceHistory, isDeviceLoading, isHistoryLoading])

    if (isDeviceLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 w-48 bg-muted rounded" />
                    <div className="h-10 w-full sm:w-96 bg-muted rounded-lg" />
                    <div className="h-64 bg-muted rounded-lg" />
                </div>
            </div>
        )
    }

    if (!device || !deviceId) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <p className="text-muted-foreground">Device not found</p>
                </div>
            </div>
        )
    }

    const handleDatesChange = (newDates: { from: string; to: string }) => {
        setDates(newDates)
    }

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <Header
                deviceId={deviceId}
                name={device?.name}
                businessId={device?.business?.id}
            />

            <AdvancedTimePicker
                dates={dates}
                onDatesChange={handleDatesChange}
                onRefresh={refetchHistory}
                isLoading={isHistoryLoading}
            />

            <div className="flex flex-col">
                {/* Data Type Filter Dropdown */}
                <div className="flex justify-end mb-4">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                                {visibleKeys.length === 0
                                    ? 'Select Data Types'
                                    : `Showing: ${visibleKeys.length} / ${Object.keys(processedData).length} types`}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <div style={{ minWidth: 220 }}>
                                <input
                                    type="text"
                                    placeholder="Search data types..."
                                    className="w-full px-2 py-1.5 mb-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                    value={searchValue}
                                    onChange={(e) =>
                                        setSearchValue(e.target.value)
                                    }
                                    autoFocus
                                />
                                <div
                                    style={{
                                        maxHeight: 260,
                                        overflowY: 'auto',
                                    }}
                                >
                                    <DropdownMenuCheckboxItem
                                        checked={
                                            visibleKeys.length ===
                                                Object.keys(processedData)
                                                    .length &&
                                            visibleKeys.length > 0
                                        }
                                        onCheckedChange={(checked) => {
                                            if (checked) {
                                                setVisibleKeys(
                                                    Object.keys(processedData)
                                                )
                                            } else {
                                                setVisibleKeys([])
                                            }
                                        }}
                                    >
                                        Select All
                                    </DropdownMenuCheckboxItem>
                                    <DropdownMenuCheckboxItem
                                        checked={visibleKeys.length === 0}
                                        onCheckedChange={() =>
                                            setVisibleKeys([])
                                        }
                                    >
                                        Deselect All
                                    </DropdownMenuCheckboxItem>
                                    <div className="border-t border-border my-1" />
                                    <div className="mb-2">
                                        <span className="text-xs text-muted-foreground px-2">
                                            Quick Selects
                                        </span>
                                        <DropdownMenuCheckboxItem
                                            checked={
                                                Object.keys(processedData)
                                                    .filter((k) =>
                                                        k.startsWith(
                                                            'Planetary'
                                                        )
                                                    )
                                                    .every((k) =>
                                                        visibleKeys.includes(k)
                                                    ) &&
                                                Object.keys(processedData).some(
                                                    (k) =>
                                                        k.startsWith(
                                                            'Planetary'
                                                        )
                                                )
                                            }
                                            onCheckedChange={(checked) => {
                                                const keys = Object.keys(
                                                    processedData
                                                ).filter((k) =>
                                                    k.startsWith('Planetary')
                                                )
                                                if (checked) {
                                                    setVisibleKeys(
                                                        Array.from(
                                                            new Set([
                                                                ...visibleKeys,
                                                                ...keys,
                                                            ])
                                                        )
                                                    )
                                                } else {
                                                    setVisibleKeys(
                                                        visibleKeys.filter(
                                                            (k) =>
                                                                !keys.includes(
                                                                    k
                                                                )
                                                        )
                                                    )
                                                }
                                            }}
                                        >
                                            Planetary
                                        </DropdownMenuCheckboxItem>
                                        <DropdownMenuCheckboxItem
                                            checked={
                                                Object.keys(processedData)
                                                    .filter((k) =>
                                                        k.startsWith(
                                                            'Satellite'
                                                        )
                                                    )
                                                    .every((k) =>
                                                        visibleKeys.includes(k)
                                                    ) &&
                                                Object.keys(processedData).some(
                                                    (k) =>
                                                        k.startsWith(
                                                            'Satellite'
                                                        )
                                                )
                                            }
                                            onCheckedChange={(checked) => {
                                                const keys = Object.keys(
                                                    processedData
                                                ).filter((k) =>
                                                    k.startsWith('Satellite')
                                                )
                                                if (checked) {
                                                    setVisibleKeys(
                                                        Array.from(
                                                            new Set([
                                                                ...visibleKeys,
                                                                ...keys,
                                                            ])
                                                        )
                                                    )
                                                } else {
                                                    setVisibleKeys(
                                                        visibleKeys.filter(
                                                            (k) =>
                                                                !keys.includes(
                                                                    k
                                                                )
                                                        )
                                                    )
                                                }
                                            }}
                                        >
                                            Satellite
                                        </DropdownMenuCheckboxItem>
                                        <DropdownMenuCheckboxItem
                                            checked={
                                                Object.keys(processedData)
                                                    .filter((k) =>
                                                        k.startsWith('MotorL')
                                                    )
                                                    .every((k) =>
                                                        visibleKeys.includes(k)
                                                    ) &&
                                                Object.keys(processedData).some(
                                                    (k) =>
                                                        k.startsWith('MotorL')
                                                )
                                            }
                                            onCheckedChange={(checked) => {
                                                const keys = Object.keys(
                                                    processedData
                                                ).filter((k) =>
                                                    k.startsWith('MotorL')
                                                )
                                                if (checked) {
                                                    setVisibleKeys(
                                                        Array.from(
                                                            new Set([
                                                                ...visibleKeys,
                                                                ...keys,
                                                            ])
                                                        )
                                                    )
                                                } else {
                                                    setVisibleKeys(
                                                        visibleKeys.filter(
                                                            (k) =>
                                                                !keys.includes(
                                                                    k
                                                                )
                                                        )
                                                    )
                                                }
                                            }}
                                        >
                                            MotorL
                                        </DropdownMenuCheckboxItem>
                                        <DropdownMenuCheckboxItem
                                            checked={
                                                Object.keys(processedData)
                                                    .filter((k) =>
                                                        k.startsWith('MotorR')
                                                    )
                                                    .every((k) =>
                                                        visibleKeys.includes(k)
                                                    ) &&
                                                Object.keys(processedData).some(
                                                    (k) =>
                                                        k.startsWith('MotorR')
                                                )
                                            }
                                            onCheckedChange={(checked) => {
                                                const keys = Object.keys(
                                                    processedData
                                                ).filter((k) =>
                                                    k.startsWith('MotorR')
                                                )
                                                if (checked) {
                                                    setVisibleKeys(
                                                        Array.from(
                                                            new Set([
                                                                ...visibleKeys,
                                                                ...keys,
                                                            ])
                                                        )
                                                    )
                                                } else {
                                                    setVisibleKeys(
                                                        visibleKeys.filter(
                                                            (k) =>
                                                                !keys.includes(
                                                                    k
                                                                )
                                                        )
                                                    )
                                                }
                                            }}
                                        >
                                            MotorR
                                        </DropdownMenuCheckboxItem>
                                    </div>
                                    <div className="border-t border-border my-1" />
                                    {Object.keys(processedData)
                                        .filter((key) => {
                                            const q = searchValue.toLowerCase()
                                            return (
                                                key.toLowerCase().includes(q) ||
                                                getDisplayName(key)
                                                    .toLowerCase()
                                                    .includes(q)
                                            )
                                        })
                                        .map((key) => (
                                            <DropdownMenuCheckboxItem
                                                key={key}
                                                checked={visibleKeys.includes(
                                                    key
                                                )}
                                                onCheckedChange={(checked) => {
                                                    setVisibleKeys((prev) =>
                                                        checked
                                                            ? [...prev, key]
                                                            : prev.filter(
                                                                  (k) =>
                                                                      k !== key
                                                              )
                                                    )
                                                }}
                                                onSelect={(e) =>
                                                    e.preventDefault()
                                                }
                                            >
                                                {getDisplayName(key)}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                </div>
                            </div>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {isHistoryError && (
                <Alert variant="destructive" className="mb-4">
                    <AlertDescription>
                        Error loading device history:{' '}
                        {historyError instanceof Error
                            ? historyError?.response?.data?.message
                            : 'Unknown error'}
                    </AlertDescription>
                </Alert>
            )}

            {loading ? (
                <div className="rounded-lg border border-dashed p-10 text-center">
                    <div className="animate-pulse">
                        <div className="h-48 bg-muted rounded-lg" />
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">
                        Loading data...
                    </p>
                </div>
            ) : Object.keys(processedData).length > 0 &&
              visibleKeys.length > 0 ? (
                <div className="rounded-lg border bg-card overflow-hidden">
                    <GraphComponent
                        data={Object.fromEntries(
                            Object.entries(processedData).filter(([key]) =>
                                visibleKeys.includes(key)
                            )
                        )}
                        displayNameMap={displayNameMap}
                    />
                </div>
            ) : (
                <div className="rounded-lg border border-dashed p-8 text-center">
                    <p className="text-sm font-medium text-foreground mb-1">
                        No data available
                    </p>
                    <p className="text-xs text-muted-foreground">
                        Try selecting a different time range, data type, or
                        refreshing the data.
                    </p>
                </div>
            )}
        </div>
    )
}

export { SingleDeviceHistoryRoute }
